import {Injectable, OnDestroy} from "@angular/core";
import {AbstractControl, FormGroup} from "@angular/forms";
import {
    ConfigurationService,
    DataResourceModel,
    LocalisableEntityData,
    ValidationService
} from "@fedeltapointofsale/data-client";
import {BehaviorSubject, Observable, ReplaySubject, Subject, Subscription} from "rxjs";
import {EntityFormControl} from "../entity-form-builder/entity-form-control";
import {EditMode} from "./edit-mode";
import {Config} from "../../../../config";
import {AnalyticEvent} from "../../../../analytics/analytic-event";
import {AnalyticsService} from "../../../../analytics/analytics.service";

export interface ControlRestrictor {
    controlRestricted: (formControl: EntityFormControl) => Promise<boolean>;
}

export function isControlRestrictor(object: any): object is ControlRestrictor {
    return "controlRestricted" in object;
}

/**
 * Provides some functions for entity edit
 */
@Injectable()
export class EditService implements OnDestroy {
    submitted: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private _editMode: EditMode;
    editMode$: ReplaySubject<EditMode> = new ReplaySubject<EditMode>(1);

    cloning: boolean;

    private _formGroup: FormGroup;

    registeredFormControls: AbstractControl[] = [];

    private formGroupSubscription: Subscription;

    private openGridEdits: number = 0;

    private _dirty: boolean;
    private _$dirtyChange: Subject<boolean> = new BehaviorSubject(false);
    public $dirtyChange: Observable<boolean> = this._$dirtyChange.asObservable();

    private _$openGridEdits: Subject<number> = new BehaviorSubject(0);
    public $openGridEdits: Observable<number> = this._$openGridEdits.asObservable();

    private _entityRestricted: boolean = false;
    $entityRestricted: Subject<boolean> = new BehaviorSubject(false);

    constructor(private validationService: ValidationService,
                private configurationService: ConfigurationService,
                private analyticsService: AnalyticsService,
                private model: DataResourceModel<any>) {
    }

    ngOnDestroy(): void {
        if (this.formGroupSubscription) {
            this.formGroupSubscription.unsubscribe();
        }
    }

    get formGroup(): FormGroup {
        return this._formGroup;
    }

    set formGroup(value: FormGroup) {
        if (this.formGroupSubscription) {
            this.formGroupSubscription.unsubscribe();
        }
        this._formGroup = value;
        this._dirty = this._formGroup.dirty;
        this.formGroupSubscription = this._formGroup.statusChanges.subscribe(() => this.dirty = this.dirty || this._formGroup.dirty);
    }

    /**
     * Confirm that the edit has been submitted
     */
    confirmSubmission(): void {
        this.submitted.next(true);
    }

    resetSubmission(): void {
        this.submitted.next(false);
    }

    /**
     * Returns if a field is required
     * @param {FormControlName} formControlName
     * @returns {Promise<boolean>}
     */
    isFieldRequired(formControl: EntityFormControl): Promise<boolean> {
        return this.validationService.isFieldRequired(this.model.data, formControl.path);
    }

    registerFormControl(formControl: AbstractControl): void {
        this.registeredFormControls.push(formControl);
    }

    deregisterFormControl(formControl: AbstractControl): void {
        this.registeredFormControls = this.registeredFormControls.filter((value) => value !== formControl);
    }

    get dirty(): boolean {
        return this._dirty;
    }

    set dirty(value: boolean) {
        this._dirty = value;
        this._$dirtyChange.next(value);
    }

    gridEditOpened() {
        this.openGridEdits++;
        this._$openGridEdits.next(this.openGridEdits);
    }

    gridEditClosed() {
        this.openGridEdits--;
        this._$openGridEdits.next(this.openGridEdits);
    }

    get editMode(): EditMode {
        return this._editMode;
    }

    set editMode(value: EditMode) {
        this._editMode = value;
        this.editMode$.next(value);
    }

    updateValueAndValidity(formControlNames?: string[]): void {
        if (formControlNames) {
            formControlNames.forEach((formControlName) => this._formGroup.get(formControlName).updateValueAndValidity());
        } else {
            this._formGroup.updateValueAndValidity();
        }
    }

    markAsTouched(formControlNames: string[]): void {
        formControlNames.forEach((formControlName) => this._formGroup.get(formControlName).markAsTouched({onlySelf: true}));
    }

    get entityRestricted(): boolean {
        return this._entityRestricted;
    }

    set entityRestricted(value: boolean) {
        this._entityRestricted = value;
        this.$entityRestricted.next(value);
    }

    restrictAllControls(formGroup: FormGroup): void {
        for (const key in formGroup.controls) {
            const formControl: AbstractControl = formGroup.controls[key];
            if (formControl instanceof FormGroup) {
                this.restrictAllControls(formControl)
            } else if (formControl instanceof EntityFormControl) {
                formControl.restricted = true;
            }
        }
    }

    public async restrictControls(formGroup: FormGroup, trackView: boolean = false): Promise<void> {
        const localisationEnabled: boolean = await this.configurationService.getBoolean(Config.LOCALISATION);
        this.entityRestricted = this.editMode === EditMode.VIEW || (localisationEnabled && this.model.data instanceof LocalisableEntityData && this.model.data.localisableContext.restricted);
        if (trackView) {
            if (this.entityRestricted) {
                this.analyticsService.trackEvent(AnalyticEvent.ENTITY_VIEW_RESTRICTED, this.model);
            } else {
                this.analyticsService.trackEvent(AnalyticEvent.ENTITY_VIEW, this.model);
            }
        }
        for (const key in formGroup.controls) {
            const formControl: AbstractControl = formGroup.controls[key];
            if (formControl instanceof FormGroup) {
                await this.restrictControls(formControl)
            } else if (formControl instanceof EntityFormControl) {
                formControl.restricted = (this.editMode === EditMode.VIEW ||
                    (isControlRestrictor(this.model) && await this.model.controlRestricted.bind(this.model)(formControl)) ||
                    (localisationEnabled && this.model.data instanceof LocalisableEntityData && this.model.data.isRestricted(formControl.path)));
            }
        }
    }

    public isFormValid(): boolean {
        return this.registeredFormControls.find((value) => value.invalid) == undefined;
    }

    public isFormPending(): boolean {
        return this.registeredFormControls.find((value) => value.pending) !== undefined;
    }
}
